import { CommonModule } from '@angular/common';
import { Component, Inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import {
    MAT_DIALOG_DATA, MatDialogRef,
} from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import { JsonPathViewerComponent } from '../../../common/json-path-viewer/json-path-viewer.component';
import { BaseComponent } from '@nucleus/ui-base';
import { PageHeaderComponent } from '@nucleus/ui-base';
import { UserData } from '../../../api/user-data-service';

export interface UserDataDialogData {
    userData: UserData;
}

/**
 * Read-only view of a UserData row. Canonical bw-dialog shell + page-header
 * (per the dialog-headers rule) so the chrome matches every other dialog.
 *
 * Header carries the type:key + a subtitle line of meta (label / resource
 * status / timestamps) and two trailing actions:
 *   - Copy JSON: writes the parsed payload to the clipboard, prettified.
 *   - X (close): the only close affordance — the bottom Close button was
 *     removed because it sat on a low-contrast strip in dark mode.
 */
@Component({
    selector: 'app-user-data-view',
    standalone: true,
    imports: [
        CommonModule, FormsModule,
        MatFormFieldModule, MatInputModule, MatButtonModule, MatIconModule,
        MatTooltipModule,
        JsonPathViewerComponent, PageHeaderComponent,
    ],
    templateUrl: './user-data-view.component.html',
    styleUrl: './user-data-view.component.css',
})
export class UserDataViewComponent extends BaseComponent {

    public jsonData: any;
    userData: UserData;

    /** Visual feedback for the Copy button — flips for ~1.4s after a copy
     *  so the icon swaps to a check and the tooltip says "Copied!". */
    copiedFlash = signal(false);

    constructor(
        public dialogRef: MatDialogRef<UserDataViewComponent>,
        @Inject(MAT_DIALOG_DATA) public data: UserDataDialogData,
    ) {
        super();
        this.userData = data.userData;
        this.jsonData = data.userData.data ? JSON.parse(data.userData.data) : null;
    }

    /** Copy the prettified JSON payload to the clipboard. Uses the modern
     *  Clipboard API; falls back to the showError toast on permission
     *  failure (no synchronous textarea hack — modern browsers all have
     *  navigator.clipboard available over HTTPS). */
    copyJson(): void {
        if (!this.jsonData) {
            this.showWarning('Row has no JSON payload to copy.');
            return;
        }
        const pretty = JSON.stringify(this.jsonData, null, 2);
        navigator.clipboard.writeText(pretty).then(
            () => {
                this.copiedFlash.set(true);
                setTimeout(() => this.copiedFlash.set(false), 1400);
                this.showSuccess('JSON copied to clipboard.');
            },
            (err) => this.showError('Could not copy: ' + (err?.message ?? err)),
        );
    }
}
